import "@fontsource/lxgw-wenkai-mono-tc"
import "@fontsource-variable/lora"
import "@fontsource-variable/roboto-mono"
import * as Sentry from "@sentry/react"
import { Suspense, useCallback, useMemo, useReducer, useRef } from "react"
import ReactDOM from "react-dom/client"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"

import { SetPassword } from "./account/SetPassword.tsx"
import { ALL_APPLICATIONS_ROUTES_CONFIG } from "./config/config.ts"
import { E2E_TEST, IS_PROD } from "./config/constants.ts"
import { computeIsMobile, useIsMobile } from "./hooks/is-mobile.ts"
import { IS_AUTH } from "./modules/auth.ts"
import { DevelopmentComponent } from "./modules/DevelopmentComponent.tsx"
import { initLogger, logger } from "./modules/logger/logger.ts"
import {
  type Settings,
  settingsContextInitialValue,
  SettingsContextProvider,
} from "./modules/settings.ts"
import { Storage } from "./modules/storage.ts"
import { updateBrowserTitle } from "./modules/utils/update-browser-title.ts"
import { RouteErrorBoundary } from "./shared-components/route-error-boundary"
import { SharedLayout } from "./shared-components/shared-layout"
import { Toaster } from "./shared-components/ui/toast"
import { ChallengesApp } from "./sortaballs-challenges/ChallengesApp.tsx"
import { SPEAK_WORDS_OF_SENTENCE_CONFIG } from "./speak-words-of-sentence/config.ts"
import "./styles/style.css"

Sentry.init({
  dsn: "https://65d8407cb191eb18680bfc95b14fa218@o4507555850420224.ingest.de.sentry.io/4510289880219728",
  sendDefaultPii: true,
})
// When running under e2e tests, return a constant so that any shuffle/random
// call produces deterministic results, keeping screenshot comparisons stable.
if (E2E_TEST) {
  Math.random = ()=> 0.5
}

initLogger()
// Light-theme background differs by auth: guests get a plain white page,
// authenticated users keep the warm off-white. Mirror the `.dark` toggle so
// CSS can key the background off this class (auth = password snapshot).
document.documentElement.classList.toggle("guest", !IS_AUTH)
const initialRoute = updateBrowserTitle()
logger({ input: `init currentRoute: ${initialRoute}`, level: "info" })

const initialAppSettings = Storage.getAppSettings() satisfies AppSettings
const initialIndexes = Storage.getIndexes() satisfies Indexes

const initialSettings = {
  ...settingsContextInitialValue,
  appSettings: initialAppSettings,
  indexes: initialIndexes,
  // SAFETY: settingsContextInitialValue merged with app/index settings matches Settings
  // SAFETY: settingsContextInitialValue merged with app/index settings matches Settings
  // E2E-only: let tests simulate the prod-mobile first visit (sidebar open at
  // mount) so the tour "wait for the game to be visible" gate is testable.
  isVisible:
    (IS_PROD && computeIsMobile()) ||
    (E2E_TEST && localStorage.getItem("e2e-initial-sidebar-open") === "ON"),
  } as Settings

type Action =
  | { payload: boolean; type: "setIsVisible" }
  | { payload: ChangeAppSettingsPayload; type: "setAppSettings" }
  | { payload: number; type: "setIndex" }

const reducer = (state: Settings, action: Action): Settings=> {
  switch (action.type) {
    case "setAppSettings": {
      const newState = {
        ...state,
        appSettings: {
          ...state.appSettings,
          [action.payload.key]: action.payload.data,
        },
      }
      return newState
    }
    case "setIndex":
      return { ...state, index: action.payload }
    case "setIsVisible":
      if (state.isVisible === action.payload) return state
      return { ...state, isVisible: action.payload }
    default:
      return state
  }
}

function WholeApp() {
  const isMobile = useIsMobile()
  const [{ appSettings, index, isVisible }, dispatch] = useReducer(
    reducer,
    initialSettings,
  )
  const appSettingsRef = useRef(appSettings)
  const setAppSettings = useCallback(
    (
      key: string,
      newValue: boolean | number | Record<string, number> | string,
      settingKey: AppSettingsKey,
    )=> {
      const newData = {
        // SAFETY: appSettingsRef.current is initialized as AppSettings
        ...(appSettingsRef.current as AppSettings)[settingKey],
        [key]: newValue,
      }
      const newAppSettings = {
        ...appSettingsRef.current,
        [settingKey]: newData,
      }
      appSettingsRef.current = newAppSettings
      Storage.setAppSettings(newAppSettings)
      dispatch({
        payload: { data: newData, key: settingKey },
        type: "setAppSettings",
      })
    },
    [],
  )
  const setIsVisible = useCallback((next: boolean)=> {
    dispatch({
      payload: next,
      type: "setIsVisible",
    })
  }, [])

  const updateAppSettings = useCallback(
    (settingKey: AppSettingsKey, newValue: AppSettings[AppSettingsKey])=> {
      const newAppSettings = {
        ...appSettingsRef.current,
        [settingKey]: newValue,
      }
      appSettingsRef.current = newAppSettings
      Storage.setAppSettings(newAppSettings)
      dispatch({
        payload: { data: newValue, key: settingKey },
        type: "setAppSettings",
      })
    },
    [],
  )
  const setIndexInStorage = useCallback((key: IndexesKey, newValue: number)=> {
    const newIndexData = {
      ...appSettingsRef.current.index,
      [key]: newValue,
    }
    const newAppSettings = { ...appSettingsRef.current, index: newIndexData }
    appSettingsRef.current = newAppSettings
    Storage.setAppSettings(newAppSettings)
    dispatch({
      payload: { data: newIndexData, key: "index" },
      type: "setAppSettings",
    })
  }, [])
  const setIndex = useCallback((index: number)=> {
    dispatch({ payload: index, type: "setIndex" })
  }, [])

  const value = useMemo(
    ()=> ({
      ...settingsContextInitialValue,
      appSettings,
      index,
      isVisible,
      setAppSettings,
      setIndex,
      setIndexInStorage,
      setIsVisible,
      updateAppSettings,
    }),
    [
      appSettings,
      index,
      isVisible,
      setAppSettings,
      setIndex,
      setIndexInStorage,
      setIsVisible,
      updateAppSettings,
    ],
  )

  return (
    <SettingsContextProvider value={value}>
      <BrowserRouter>
        <Toaster />
        <Routes>
          <Route element={<SharedLayout triggerFullscreen={isMobile} />}>
            <Route
              element={
                <Navigate
                  replace={true}
                  to={SPEAK_WORDS_OF_SENTENCE_CONFIG.route}
                />
              }
              path="/"
            />
            {ALL_APPLICATIONS_ROUTES_CONFIG.map(route=> (
              <Route
                element={
                  <RouteErrorBoundary>
                    <Suspense fallback={<div>Loading…</div>}>
                      <route.component {...(route.additionalProps ?? {})} />
                    </Suspense>
                  </RouteErrorBoundary>
                }
                key={route.key}
                path={route.route}
              />
            ))}
            {/* Parameterized battle pages: the battle itself is restored from
                session storage, so a reload mid-game resumes where it left off. */}
            <Route
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<div>Loading…</div>}>
                    <ChallengesApp />
                  </Suspense>
                </RouteErrorBoundary>
              }
              key="create-challenge"
              path="/create-challenge/:id"
            />
            <Route
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<div>Loading…</div>}>
                    <ChallengesApp />
                  </Suspense>
                </RouteErrorBoundary>
              }
              key="accept-challenge"
              path="/accept-challenge/:id"
            />
            <Route
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<div>Loading…</div>}>
                    <SetPassword />
                  </Suspense>
                </RouteErrorBoundary>
              }
              key="set-password"
              path="/password/:value"
            />
            <Route element={<DevelopmentComponent />} path="/foo" />
            <Route element={<Navigate replace to="/" />} path="*" />
          </Route>
        </Routes>
      </BrowserRouter>
    </SettingsContextProvider>
  )
}

const rootEl = document.getElementById("root")
if (rootEl) {
  ReactDOM.createRoot(rootEl).render(<WholeApp />)
} else {
  console.error("root element not found")
}
